import { cn } from '@newcore/ui/cn';
import { Sizes } from '@newcore/ui/enums/sizes';
import { Tooltip } from '@newcore/ui/tooltips';
import { AppIcon } from '@/components/common/app-icon';
import { SvgIcon } from '@/components/ui/icons';
import { SsoType } from '@/enums/sso-type.enums';
import type { UserApplication } from '@/types/api.types';
import { getApplicationIconUrl } from '@/utils/icon-utils';

type AppIconBoxSize = Sizes.SM | Sizes.MD | Sizes.LG;

const BOX_SIZE_CLASSES: Record<AppIconBoxSize, string> = {
  [Sizes.SM]: 'size-10',
  [Sizes.MD]: 'size-12',
  [Sizes.LG]: 'size-16',
};

const BOX_RADIUS_CLASSES: Record<AppIconBoxSize, string> = {
  [Sizes.SM]: 'rounded-lg',
  [Sizes.MD]: 'rounded-xl',
  [Sizes.LG]: 'rounded-2xl',
};

const SSK_BADGE_SIZES: Record<AppIconBoxSize, Sizes.SM | Sizes.MD> = {
  [Sizes.SM]: Sizes.SM,
  [Sizes.MD]: Sizes.SM,
  [Sizes.LG]: Sizes.MD,
};

const SSK_SSO_TYPES = new Set<UserApplication['sso_type']>([
  SsoType.SAML_SSK,
  SsoType.OIDC_SSK,
]);

interface AppIconBoxProps {
  application: UserApplication;
  size: AppIconBoxSize;
  className?: string;
}

export function AppIconBox({ application, size, className }: AppIconBoxProps) {
  const isSskProtected = SSK_SSO_TYPES.has(application.sso_type);

  return (
    <span className={cn('relative block shrink-0', BOX_SIZE_CLASSES[size])}>
      <span
        className={cn(
          'flex size-full items-center justify-center overflow-hidden border border-solid border-secondary bg-primary transition-all duration-150',
          BOX_RADIUS_CLASSES[size],
          className
        )}
      >
        <AppIcon
          iconUrl={getApplicationIconUrl(
            application.icon_path,
            application.app_name
          )}
          appName={application.app_name}
          size={size}
        />
      </span>
      {isSskProtected && (
        <Tooltip content='This app is protected by Secure Split Key — signing requires your company NewCore browser extension'>
          <SvgIcon
            name='ssk'
            size={SSK_BADGE_SIZES[size]}
            aria-label='Protected with Secure Split Key'
            data-testid='ssk-badge'
            className='absolute -bottom-1 -right-1'
          />
        </Tooltip>
      )}
    </span>
  );
}
